import { useBoolean, useDisclosure } from '@chakra-ui/react';
import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ScopeType from 'utils/enums/ScopeType';

import useGetAllTRSRegistrations from 'pages/telephony/numbers/queries/useGetAllTRSRegistrations';
import useGetAppAuthByCompanyId from '@/queries/appAuth/useGetAppAuthbyCompanyId';
import useGetCompanyServices from '@/queries/appAuth/useGetCompanyServices';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import EditPlatformNumberDrawer from './EditPlatformNumberDrawer';
import useGetNumbers from '../queries/useGetNumbers';
import useGetPlatformNumbers from '../queries/useGetPlatformNumbers';
import useGetOutboundCaller from '../queries/useGetOutboundCaller';
import useIndicateMultiActionPlatform from '../queries/useIndicateMultiActionPlatform';
import useGetOperatorConnectUsages from '../queries/useGetOperatorConnectUsages';
import useGetCompanyPlatformNumbers from '../queries/useGetCompanyPlatformNumbers';
import ConfirmNumbersModal from '../new/modals/ConfirmNumbersModal';
import NumberTableToolbar from './tableComponents/NumberTableToolbar';
import ServiceWarningBanner from 'components/maintenance/ServiceWarningBanner';

import {
    getColDefs,
    getPlatformNumberColDefs,
    getSinglePlatformNumberColDefs,
    defaultCellRendererColDef,
    getDetailCellRendererParams,
    getRowSelectionConfig,
    getNumbersQueryConfig,
    TABLE_REFRESH_THRESHOLD,
    createDataFetchWrapper,
    getLoadingState,
    computeHasPlatformSelected,
    isTableFetchInFlight,
    sanitiseSinglePlatformState,
    sanitiseMasterDetailState,
} from './tableComponents/NumberTableConfig';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
// Stable identity so the emptied grid view does not churn every render.
const NO_ROWS = [];
// Matches the naming convention used by the Numbers CSV template download
const makeDateStr = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}_${hh}${min}${ss}`;
};

const slugify = (str) =>
    (str || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

const NumberTable = ({
    menuData,
    platformList,
    expandedPlatformNumberIdRef,
    expandedPlatformNumbersRef,
    setSummaryData,
    countries,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [tooltipShow, setTooltipShow] = useBoolean();
    const {
        isOpen: isConfirmModalOpen,
        onOpen: onConfirmModalOpen,
        onClose: onConfirmModalClose,
    } = useDisclosure();

    const [numbersRequiringConfirmation, setNumbersRequiringConfirmation] =
        useState([]);
    const countriesArray = useMemo(() => countries || [], [countries]);

    const { currentScope } = useSelector((state) => ({ ...state.login }));
    const { currentCompany, currentWholesaler, currentPartner } = useSelector(
        (state) => ({ ...state.navigationLists }),
    );
    const { data: currentCompanyData } = useGetCurrentCompany();

    const gridRef = useRef();
    const [currentNumber, setCurrentNumber] = useState();
    const [expandedParams, setExpandedParams] = useState();
    // Bumped on every master row expand. Part of the platform numbers query
    // key, so re-opening the same row issues a fresh request instead of
    // resolving from cache without running the query's success callback.
    const [expansionCount, setExpansionCount] = useState(0);
    const [selectedPlatform, setSelectedPlatform] = useState();
    const [isEditPlatformDrawerOpen, setIsEditPlatformDrawerOpen] =
        useState(false);
    const [selectedOption, setSelectedOption] = useState(undefined);
    const [selectedRows, setSelectedRows] = useState([]);
    const [pendingDrawerOpen, setPendingDrawerOpen] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    const hasPlatformSelected = computeHasPlatformSelected(
        selectedOption,
        platformList,
    );

    // The Numbers page (general list and platform view alike) only supports
    // Company/Partner scope (see Navbar.jsx scopeList()), so auto-default down
    // to Partner if the user is at Wholesaler/Global while on this page.
    useEffect(() => {
        if (
            currentScope === ScopeType.WHOLESALER ||
            currentScope === ScopeType.GLOBAL
        ) {
            dispatch({
                type: 'CHANGE_CURRENT_SCOPE',
                payload: ScopeType.PARTNER,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScope]);

    const shouldFetchDrawerData = isEditPlatformDrawerOpen || pendingDrawerOpen;

    const { data: OC } = useGetOperatorConnectUsages(
        currentCompany,
        currentScope,
        shouldFetchDrawerData,
    );

    const { data: trsRegistrations, refetch: refetchTRSRegistrations } =
        useGetAllTRSRegistrations(
            currentScope,
            currentCompany,
            shouldFetchDrawerData,
        );

    const { data: appAuth } = useGetAppAuthByCompanyId(currentCompany);

    const { data: companyServices } = useGetCompanyServices(currentCompany);

    const { data: outboundCallerIDs, isFetching: isFetchingOutboundCallerIDs } =
        useGetOutboundCaller(currentCompany, shouldFetchDrawerData);

    const {
        refetch: platformRefetch,
        isFetching: isFetchingPlatformNumbers,
        isLoading: isLoadingPlatformNumbers,
    } = useGetPlatformNumbers(
        expandedPlatformNumberIdRef.current,
        currentScope,
        expandedParams,
        expandedPlatformNumbersRef,
        expansionCount,
    );

    const selectedOptionRef = useRef(selectedOption);
    const platformListRef = useRef(platformList);

    useEffect(() => {
        selectedOptionRef.current = selectedOption;
        platformListRef.current = platformList;
    }, [selectedOption, platformList]);

    // Open drawer once platform numbers have finished loading
    useEffect(() => {
        if (
            pendingDrawerOpen &&
            !isFetchingPlatformNumbers &&
            !isLoadingPlatformNumbers
        ) {
            setIsEditPlatformDrawerOpen(true);
            setPendingDrawerOpen(false);
        }
    }, [
        pendingDrawerOpen,
        isFetchingPlatformNumbers,
        isLoadingPlatformNumbers,
    ]);

    const handlePlatformEditBtn = useCallback(
        (params, isMainTable) => {
            const hasButtonPlatformSelected = computeHasPlatformSelected(
                selectedOptionRef.current,
                platformListRef.current,
            );

            if (hasButtonPlatformSelected || isMainTable) {
                const numberId = hasButtonPlatformSelected
                    ? params.data?.numberID
                    : params.data?.id;

                const editSelectedPlatform = isMainTable ? null : params.data;

                expandedPlatformNumberIdRef.current = numberId;
                setSelectedPlatform(editSelectedPlatform);
                setCurrentNumber(numberId);
                setExpandedParams(params);
                setPendingDrawerOpen(true);
            } else {
                setSelectedPlatform(params.data);
                setCurrentNumber(params.data?.numberID);
                setIsEditPlatformDrawerOpen(true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [platformRefetch],
    );

    const baseColumns = useMemo(
        () =>
            getColDefs(
                countriesArray,
                currentScope,
                darkMode,
                handlePlatformEditBtn,
            ),
        [countriesArray, currentScope, darkMode, handlePlatformEditBtn],
    );

    const basePlatformNumberColumns = useMemo(
        () =>
            getPlatformNumberColDefs(
                darkMode,
                handlePlatformEditBtn,
                hasPlatformSelected,
                countriesArray,
            ),
        [countriesArray, darkMode, handlePlatformEditBtn, hasPlatformSelected],
    );

    const singlePlatformNumberColumns = useMemo(
        () =>
            getSinglePlatformNumberColDefs(
                darkMode,
                handlePlatformEditBtn,
                hasPlatformSelected,
                countriesArray,
            ),
        [countriesArray, darkMode, handlePlatformEditBtn, hasPlatformSelected],
    );

    const handleDetailRowExpand = useCallback(
        (params) => {
            expandedPlatformNumberIdRef.current = params.data.id;
            setExpandedParams(params);
            setExpansionCount((count) => count + 1);
        },
        [expandedPlatformNumberIdRef],
    );

    const detailCellRendererParams = useMemo(
        () =>
            getDetailCellRendererParams(
                basePlatformNumberColumns,
                defaultCellRendererColDef,
                setSelectedRows,
                handleDetailRowExpand,
            ),
        [basePlatformNumberColumns, setSelectedRows, handleDetailRowExpand],
    );

    const [tableData, setTableData] = useState({
        columns: baseColumns,
        data: [],
        detailCellRendererParams: detailCellRendererParams,
    });

    const [platformsTableData, setPlatformsTableData] = useState({
        columns: singlePlatformNumberColumns,
        data: [],
    });

    const numbersQueryConfig = getNumbersQueryConfig(
        currentScope,
        currentWholesaler,
        currentPartner,
        currentCompany,
    );

    const numbersDataFetched = useRef(false);
    const platformNumbersDataFetched = useRef(false);

    // NOTE: quick fix for infinite loading state
    // Where platformList: [] && isLoading: true -> isInitialLoading = isLoading && isFetching
    const {
        isInitialLoading: isLoading,
        isRefetching,
        isFetching,
        refetch,
    } = useGetNumbers(
        numbersQueryConfig.scope,
        numbersQueryConfig.wholesaler,
        numbersQueryConfig.partner,
        numbersQueryConfig.company,
        createDataFetchWrapper(setTableData, numbersDataFetched),
        setSummaryData,
        platformList,
        hasPlatformSelected,
    );

    const {
        isLoading: isLoadingCompanyPlatformNumbers,
        isRefetching: isRefetchingPlatformNumbers,
        isFetching: isFetchingCompanyPlatformNumbers,
        refetch: refetchCompanyPlatformNumbers,
    } = useGetCompanyPlatformNumbers(
        currentCompany,
        currentPartner,
        selectedOption,
        currentScope,
        platformList,
        setPlatformsTableData,
        setSummaryData,
        platformNumbersDataFetched,
    );

    // Platform selection logic
    const hasSetDefaultPlatform = useRef(false);

    useEffect(() => {
        setSelectedOption(undefined);
        hasSetDefaultPlatform.current = false;
        numbersDataFetched.current = false;
        platformNumbersDataFetched.current = false;
    }, [currentCompany]);

    // Company- and partner-level platform lists can differ, so a platform picked
    // under the previous scope may not exist under the new one — and an empty
    // list means none do. Dropping the selection resets the filter to the full
    // numbers list. Done during render so the platform numbers query never fires
    // for a platform the current scope cannot see.
    if (
        platformList !== undefined &&
        selectedOption !== undefined &&
        !platformList.some((platform) => platform.id === selectedOption.id)
    ) {
        setSelectedOption(undefined);
        hasSetDefaultPlatform.current = false;
    }

    useEffect(() => {
        if (platformList?.length !== 1) {
            return;
        }
        if (
            !hasSetDefaultPlatform.current &&
            platformList[0]?.id !== undefined
        ) {
            setSelectedOption(platformList[0]);
            hasSetDefaultPlatform.current = true;
        }
    }, [platformList]);

    // selectedOption is intentionally NOT reset on a scope change — the selected
    // platform should survive it so its numbers refetch under the new scope.
    //
    // Scope and selected platform both re-key the query feeding the grid, so
    // both re-arm the overlay. Mirrors useGetCompanyPlatformNumbers' own
    // platformId so the key tracks the request that is actually issued.
    const activePlatformId =
        platformList?.length === 1 ? platformList[0]?.id : selectedOption?.id;
    const tableSourceKey = `${currentScope}:${activePlatformId ?? 'all'}`;

    // Set during render, not in an effect: the query hooks copy their results
    // into the mirrored state from their own effects, which React flushes first.
    const [sourceAtLastSwitch, setSourceAtLastSwitch] =
        useState(tableSourceKey);
    const [isSwitchingSource, setIsSwitchingSource] = useState(false);

    if (sourceAtLastSwitch !== tableSourceKey) {
        setSourceAtLastSwitch(tableSourceKey);
        setIsSwitchingSource(true);
        setSelectedRows([]);
        numbersDataFetched.current = false;
        platformNumbersDataFetched.current = false;
    }

    // Whichever query actually feeds the table under the current view.
    const isActiveFetchInFlight = isTableFetchInFlight(
        platformList,
        hasPlatformSelected,
        isLoading,
        isRefetching,
        isFetching,
        isLoadingCompanyPlatformNumbers,
        isRefetchingPlatformNumbers,
        isFetchingCompanyPlatformNumbers,
    );

    // Ends the switch once its refetch settles. A query that never issues a
    // request clears the flag as soon as it is enabled — no overlay rather than
    // a stuck one.
    useEffect(() => {
        if (isSwitchingSource && !isActiveFetchInFlight) {
            setIsSwitchingSource(false);
        }
    }, [isSwitchingSource, isActiveFetchInFlight]);

    // Loading state — keyed off hasPlatformSelected so the overlay follows
    // whichever query is actually feeding the table (a platform picked from a
    // multi-platform list is served by the platform numbers query as well).
    const isFetchingData =
        isSwitchingSource ||
        getLoadingState(
            hasPlatformSelected,
            numbersDataFetched,
            platformNumbersDataFetched,
            isLoading,
            isRefetching,
            isFetching,
            isLoadingCompanyPlatformNumbers,
            isRefetchingPlatformNumbers,
            isFetchingPlatformNumbers,
            gridRef,
            platformList,
        );

    // Use hasPlatformSelected (not selectedOption !== undefined) so that the
    // single-platform case is handled correctly even before the selectedOption
    // useEffect has had a chance to run.
    const refetchTableData = hasPlatformSelected
        ? refetchCompanyPlatformNumbers
        : refetch;

    const { mutate: indicateMultiActionPlatformNumber } =
        useIndicateMultiActionPlatform(
            selectedRows,
            currentScope,
            setIsConfirming,
            refetchTableData,
        );

    const handleSelectedActionNumber = () => {
        setIsConfirming(true);
        indicateMultiActionPlatformNumber();
    };

    // Effects
    // useEffect(() => {
    //     refetchTableData();
    // }, [indicateMultiActionPlatformNumber, refetchTableData]);

    const tableDataWithColumns = useMemo(
        () => ({
            columns: baseColumns,
            data: tableData.data,
            detailCellRendererParams: detailCellRendererParams,
        }),
        [baseColumns, tableData.data, detailCellRendererParams],
    );

    const platformsTableDataWithColumns = useMemo(
        () => ({
            columns: singlePlatformNumberColumns,
            data: platformsTableData.data,
        }),
        [singlePlatformNumberColumns, platformsTableData.data],
    );

    const selectedTableData = hasPlatformSelected
        ? platformsTableDataWithColumns
        : tableDataWithColumns;

    // AGGridMain only paints its overlay over an empty grid, so empty it while a
    // scope switch is in flight — a cached scope otherwise refills instantly and
    // the refetch runs with no loading shown. Only the grid sees this; row
    // selection, drawer refresh and confirm all read the real rows.
    const gridTableData = useMemo(
        () =>
            isSwitchingSource
                ? { ...selectedTableData, data: NO_ROWS }
                : selectedTableData,
        [isSwitchingSource, selectedTableData],
    );

    const refreshTableDataFromDrawer = () => {
        selectedTableData?.data?.length < TABLE_REFRESH_THRESHOLD &&
            refetchTableData();
    };

    // Modal handlers
    const handleConfirm = useCallback(() => {
        const numbersToConfirm =
            selectedTableData?.data?.filter(
                (row) => row.platformStatus === 17,
            ) || [];

        const formattedNumbers = numbersToConfirm.map((number, index) => ({
            id: number.id || index,
            telephoneNumber: number.telephoneNumber,
            status: 'pending_confirmation',
            platformStatus: number.platformStatus,
            numberID: number.numberID,
            platformID: number.platformID,
            ...number,
        }));

        setNumbersRequiringConfirmation(formattedNumbers);
        onConfirmModalOpen();
    }, [selectedTableData?.data, onConfirmModalOpen]);

    const handleConfirmComplete = useCallback(() => {
        refetchTableData();
        onConfirmModalClose();
    }, [refetchTableData, onConfirmModalClose]);

    const handleConfirmModalClose = useCallback(() => {
        setNumbersRequiringConfirmation([]);
        onConfirmModalClose();
    }, [onConfirmModalClose]);

    // Matches the Numbers CSV template naming pattern, e.g. Acme_Numbers_260812_143022
    const titleName = `${
        slugify(currentCompanyData?.name) || 'Company'
    }_Numbers_${makeDateStr()}`;

    const rowSelectionConfig = useMemo(
        () => getRowSelectionConfig(hasPlatformSelected, selectedTableData),
        [hasPlatformSelected, selectedTableData],
    );

    return (
        <>
            <ServiceWarningBanner
                appAuth={appAuth}
                companyServices={companyServices}
                context="Number Management"
            />
            <AGGridMain
                gridRef={gridRef}
                tableData={gridTableData}
                dataSourceKey={tableSourceKey}
                masterDetail={hasPlatformSelected ? false : true}
                isLoading={isFetchingData}
                refetch={refetchTableData}
                isRowMaster={
                    platformList?.length === 0
                        ? (data) => data.platformNumbers?.length > 0
                        : undefined
                }
                stateIdentifier={
                    hasPlatformSelected
                        ? 'Numbers-PlatformView'
                        : 'Numbers-MasterDetailView'
                }
                sanitiseState={
                    hasPlatformSelected
                        ? sanitiseSinglePlatformState
                        : sanitiseMasterDetailState
                }
                overlayNoRowsTemplate="No Numbers Found"
                rowSelection={rowSelectionConfig}
                onSelectionChanged={(params) => {
                    setSelectedRows(params.api.getSelectedRows());
                }}
                titleName={titleName}
                isSaveButtonEnabled
                extraComponents={
                    <NumberTableToolbar
                        currentScope={currentScope}
                        selectedTableData={selectedTableData}
                        tooltipShow={tooltipShow}
                        setTooltipShow={setTooltipShow}
                        handleConfirm={handleConfirm}
                        navigate={navigate}
                        menuData={menuData}
                        platformList={platformList}
                        selectedOption={selectedOption}
                        setSelectedOption={setSelectedOption}
                        selectedRows={selectedRows}
                        handleSelectedActionNumber={handleSelectedActionNumber}
                        isConfirming={isConfirming}
                        refetchTableData={refetchTableData}
                    />
                }
            />
            <EditPlatformNumberDrawer
                key={selectedPlatform?.platformID || currentNumber}
                isOpen={isEditPlatformDrawerOpen}
                onClose={() => setIsEditPlatformDrawerOpen(false)}
                expandedPlatformNumbers={expandedPlatformNumbersRef.current}
                selectedPlatform={selectedPlatform}
                darkMode={darkMode}
                currentCompany={currentCompany}
                refreshTable={refreshTableDataFromDrawer}
                platformRefetch={platformRefetch}
                isFetchingPlatformNumbers={isFetchingPlatformNumbers}
                isLoadingPlatformNumbers={isLoadingPlatformNumbers}
                currentNumber={currentNumber}
                isFetchingOutboundCallerIDs={isFetchingOutboundCallerIDs}
                outboundCallerIDs={outboundCallerIDs}
                platformList={platformList}
                OC={OC}
                trsRegistrations={trsRegistrations}
                refetchTRSRegistrations={refetchTRSRegistrations}
                tableRowData={expandedParams?.data}
            />
            <ConfirmNumbersModal
                isOpen={isConfirmModalOpen}
                onClose={handleConfirmModalClose}
                onConfirm={handleConfirmComplete}
                initialNumbers={numbersRequiringConfirmation}
                refetch={refetchTableData}
            />
        </>
    );
};

export default NumberTable;
